import React from 'react';
import { Box, Typography } from '@mui/material';
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import * as Types from 'types/types';
import { resolveColor, ResumeStyle } from './resumePreviewStyles';

export const StyledHeader: React.FC<{
  candidate: Types.Candidate;
  style: ResumeStyle;
  isMobile: boolean;
}> = ({ candidate, style, isMobile }) => {
  const phone = parsePhoneNumberFromString(candidate.phone || '', 'US');
  const primaryColor = resolveColor(style, 'primary');
  const secondaryColor = resolveColor(style, 'secondary');
  const accentColor = resolveColor(style, 'accent');
  const textColor = resolveColor(style, 'text');

  return (
    <Box className="BackstoryResumeHeader" sx={style.headerStyle}>
      <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
        <Typography
          variant="h4"
          sx={{ fontWeight: 'bold', mb: 1, color: primaryColor, fontFamily: 'inherit' }}
        >
          {candidate.fullName}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: 'flex-start',
            gap: 1,
          }}
        >
          {candidate.description && (
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                fontWeight: 300,
                color: secondaryColor,
                fontFamily: 'inherit',
                fontSize: '0.8rem !important',
              }}
            >
              {candidate.description}
            </Typography>
          )}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flexWrap: 'wrap',
              alignContent: 'center',
              flexGrow: 1,
              minWidth: 'fit-content',
              gap: 1,
            }}
          >
            {candidate.email && (
              <Box sx={{ display: 'flex', alignItems: 'center', m: 0, p: 0 }}>
                <EmailIcon fontSize="small" sx={{ mr: 1, color: accentColor }} />
                <Typography variant="body2" sx={{ color: textColor, fontFamily: 'inherit' }}>
                  {candidate.email}
                </Typography>
              </Box>
            )}
            {phone?.isValid() && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PhoneIcon fontSize="small" sx={{ mr: 1, color: accentColor }} />
                <Typography variant="body2" sx={{ color: textColor, fontFamily: 'inherit' }}>
                  {phone.formatInternational()}
                </Typography>
              </Box>
            )}
            {candidate.location && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <LocationIcon fontSize="small" sx={{ mr: 1, color: accentColor }} />
                <Typography variant="body2" sx={{ color: textColor, fontFamily: 'inherit' }}>
                  {candidate.location.city
                    ? `${candidate.location.city}, ${candidate.location.state}`
                    : candidate.location.text}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export const StyledFooter: React.FC<{
  resume: Types.Resume;
  style: ResumeStyle;
  isMobile: boolean;
}> = ({ resume, style, isMobile }) => (
  <>
    <Box
      className="BackstoryResumeFooter"
      sx={{
        ...style.footerStyle,
        color: style.color.secondary,
        fontSize: isMobile ? '0.6rem' : '0.8rem',
      }}
    >
      Dive deeper into my qualifications at Backstory...
      <Box
        component="img"
        src={`/api/1.0/candidates/qr-code/${resume.id}`}
        alt="QR Code"
        className="qr-code"
        sx={{ display: 'flex', mt: 1, mb: 1 }}
      />
      {window.location.protocol}
      {'//'}
      {window.location.host}/chat/{resume.id}
    </Box>
    <Box sx={{ pb: 2 }}>&nbsp;</Box>
  </>
);
